import { Currency } from "@hubrise/react-components"
import type { Method } from "@hubrise/react-components"
import { QueryClient, useQuery, UseQueryResult } from "@tanstack/react-query"
import { AxiosError } from "axios"

import catalogImporter, { ImportedCatalog } from "shared/hubrise/catalogImporter"
import { IAccount, ICatalog, ICatalogSummary } from "shared/hubrise/models"
import { IUser } from "shared/utils/config"
import { axiosServer } from "shared/utils/server"

const ONE_MINUTE = 60 * 1000

export const queryClient = new QueryClient()

export const useAccountQuery = (appInstanceId: string): UseQueryResult<IAccount, AxiosError> => {
  return useServerQuery<IAccount>("GET", "/api/account", appInstanceId, { staleTime: 20 * ONE_MINUTE })
}

export const useUserQuery = (appInstanceId: string): UseQueryResult<IUser, AxiosError> => {
  return useServerQuery<IUser>("GET", "/user", appInstanceId, { staleTime: 20 * ONE_MINUTE })
}

export const useCatalogSummariesQuery = (appInstanceId: string): UseQueryResult<ICatalogSummary[], AxiosError> => {
  return useServerQuery<ICatalogSummary[]>("GET", "/api/catalogs", appInstanceId, { staleTime: ONE_MINUTE })
}

export const useCatalogQuery = (
  catalogId: string,
  appInstanceId: string,
  currency: Currency,
  onServerChange?: OnServerChange<ICatalog>,
): UseQueryResult<ICatalog, AxiosError> => {
  return useServerQuery<ICatalog, ImportedCatalog>("GET", `/api/catalogs/${catalogId}`, appInstanceId, {
    staleTime: 5 * ONE_MINUTE,
    onServerChange,
    postProcess: (data) => catalogImporter(data, currency),
  })
}

type OnServerChange<Data> = (newData: Data) => void

type PostProcess<Data, ServerData> = (serverData: ServerData) => Data

type UseServerOptions<Data, ServerData> = {
  staleTime: number
  onServerChange?: OnServerChange<Data>
  postProcess?: PostProcess<Data, ServerData>
}

function useServerQuery<Data, ServerData = Data>(
  method: Method,
  path: string,
  appInstanceId: string,
  { staleTime, onServerChange, postProcess }: UseServerOptions<Data, ServerData>,
): UseQueryResult<Data, AxiosError> {
  return useQuery<Data, AxiosError>({
    queryKey: [method, path],
    queryFn: async () => {
      const { data: serverData } = await axiosServer<ServerData>(method, path, appInstanceId)
      const data: Data = postProcess ? postProcess(serverData) : (serverData as any as Data)

      if (onServerChange) onServerChange(data)

      return data
    },
    staleTime,
  })
}
